import { BoxFolder } from "@modules/box/types/index.js";
import { BriefExportParts } from "@modules/command-center/types";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../tailwind.config.js";
import { CareerStageOption } from "@common/types";
import { Config } from "tailwindcss/types/config.js";
import { DefaultColors } from "tailwindcss/types/generated/colors.js";

const enum CareerStage {
  MEDICAL_STUDENT = "MEDICAL_STUDENT",
  RESIDENT = "RESIDENT",
  RETIRED_PHYSICIAN = "RETIRED_PHYSICIAN",
  PRACTICING_PHYSICIAN = "PRACTICING_PHYSICIAN",
}

const enum CareerStageExpanded {
  MEDICAL_STUDENT = "MEDICAL_STUDENT",
  RESIDENT = "RESIDENT",
  RETIRED_PHYSICIAN = "RETIRED_PHYSICIAN",
  PRACTICING_PHYSICIAN_EARLY = "PRACTICING_PHYSICIAN_EARLY",
  PRACTICING_PHYSICIAN_MID = "PRACTICING_PHYSICIAN_MID",
  PRACTICING_PHYSICIAN_LATE = "PRACTICING_PHYSICIAN_LATE",
}

const CareerStageMapping: Record<CareerStage | CareerStageExpanded, string> = {
  [CareerStage.MEDICAL_STUDENT]: "Medical Student",
  [CareerStage.RESIDENT]: "Resident",
  [CareerStage.RETIRED_PHYSICIAN]: "Retired Physician",
  [CareerStage.PRACTICING_PHYSICIAN]: "Practicing Physician",
  [CareerStageExpanded.PRACTICING_PHYSICIAN_EARLY]:
    "Early-Stage Practicing Physician",
  [CareerStageExpanded.PRACTICING_PHYSICIAN_MID]:
    "Mid-Stage Practicing Physician",
  [CareerStageExpanded.PRACTICING_PHYSICIAN_LATE]:
    "Late-Stage Practicing Physician",
};

const CareerStageOptions: CareerStageOption[] = [
  {
    id: null,
    name: "Make a Selection",
    value: null,
    label: "Make a Selection",
  },
  {
    id: CareerStage.MEDICAL_STUDENT,
    name: CareerStageMapping[CareerStage.MEDICAL_STUDENT],
    value: CareerStage.MEDICAL_STUDENT,
    label: CareerStageMapping[CareerStage.MEDICAL_STUDENT],
  },
  {
    id: CareerStage.RESIDENT,
    name: CareerStageMapping[CareerStage.RESIDENT],
    value: CareerStage.RESIDENT,
    label: CareerStageMapping[CareerStage.RESIDENT],
  },
  {
    id: CareerStage.PRACTICING_PHYSICIAN,
    name: CareerStageMapping[CareerStage.PRACTICING_PHYSICIAN],
    value: CareerStage.PRACTICING_PHYSICIAN,
    label: CareerStageMapping[CareerStage.PRACTICING_PHYSICIAN],
  },
  {
    id: CareerStage.RETIRED_PHYSICIAN,
    name: CareerStageMapping[CareerStage.RETIRED_PHYSICIAN],
    value: CareerStage.RETIRED_PHYSICIAN,
    label: CareerStageMapping[CareerStage.RETIRED_PHYSICIAN],
  },
];

const enum AdvisorRoutes {
  Atlas = "/atlas",
  CasesAndTasks = "/cases-and-tasks",
  CommandCenter = "/command-center",
  ClientThreeSixty = "/client-360/[householdId]",
  Households = "/households",
  MeetingBriefPrint = "/command-center/brief/[eventId]",
  MyPractice = "/my-practice",
  Observability = "/atlas/observability",
  Settings = "/settings",
  TeamMetrics = "/team-metrics",
  UxtTesting = "/uxt-testing",
}

// Build a concrete Client 360 href from the route pattern. Returns a plain
// string so next/link isn't handed a dynamic `[householdId]` pathname (which
// Next rejects) — while keeping AdvisorRoutes.ClientThreeSixty the single
// source of truth for the path.
//
// viewAsAdvisorEmail carries an advisor admin's view-as selection across the
// navigation. Command Center holds it in page state, which does not survive a
// route change, and Client 360 reads it from ?viewAs= — so any link built while
// viewing as another advisor has to put it in the URL or the destination silently
// falls back to the caller's own book and 404s the demographics call.
const clientThreeSixtyHref = (
  householdId: string,
  viewAsAdvisorEmail?: string
) => {
  const path = AdvisorRoutes.ClientThreeSixty.replace(
    "[householdId]",
    encodeURIComponent(householdId)
  );
  return viewAsAdvisorEmail
    ? `${path}?viewAs=${encodeURIComponent(viewAsAdvisorEmail)}`
    : path;
};

// Build a concrete printable-brief href from the route pattern. Opened in a new
// tab from the pre-brief modal's "Export as PDF" action.
//
// `date` is the meeting's LOCAL calendar day (YYYY-MM-DD). The page re-fetches
// the brief and the meeting from the advisor Atlas API, both of which are
// date-range endpoints keyed on the meeting's start time — so the day is what
// lets the page ask for a single-day range instead of guessing a window. The
// event id is encoded because Outlook ids contain "/", "+" and "=".
//
// viewAsAdvisorEmail carries an advisor admin's view-as selection, for the same
// reason clientThreeSixtyHref does: Command Center holds it in page state, which
// does not survive opening a new tab, and both re-fetches are scoped to the
// caller's own calendar without it.
//
// `parts` selects which sections the printed page renders. Omitted for "pre",
// which is the route's own default — so an export that wants only the brief
// produces exactly the URL it always has, and the print page never fires a
// recaps request it has no use for.
const meetingBriefPrintHref = (
  eventId: string,
  date: string,
  viewAsAdvisorEmail?: string,
  parts?: BriefExportParts
) => {
  const path = AdvisorRoutes.MeetingBriefPrint.replace(
    "[eventId]",
    encodeURIComponent(eventId)
  );
  const params = new URLSearchParams({ date });
  if (viewAsAdvisorEmail) {
    params.append("viewAs", viewAsAdvisorEmail);
  }
  if (parts && parts !== "pre") {
    params.append("parts", parts);
  }
  return `${path}?${params.toString()}`;
};

// TODO: add all routes to this file
const enum ClientRoutes {
  Onboarding = "/welcome",
  Overview = "/overview",
  FinancialPlan = "/financial-plan",
  DocumentVault = "/document-vault",
  TaskAndActions = "/tasks-and-actions",
  ForYou = "/for-you",
  ForYouCalculators = "/for-you/calculators",
  AdvisoryTeam = "/advisory-team",
  Investments = "/investments",
  TaxSavingsReport = "/tax-savings-report",
  Accounts = "/accounts",
  Spending = "/spending",
  Budgets = "/budgets",
  Settings = "/settings",
  Logout = "/api/auth/logout",
  Disclosures = "/disclosures",
  PhysicianOnFire = "/physician-on-fire",
  TaxCenter = "/tax-center",
  ServiceLines = "/service-lines/[service]",
  // Currently, only used for impersonation
  Connections = "/connections",
}

const HEADER_TITLE_BY_PATH = {
  [ClientRoutes.Overview]: "360° View",
  [ClientRoutes.FinancialPlan]: "Financial Plan",
  [ClientRoutes.DocumentVault]: "Document Vault",
  [ClientRoutes.TaskAndActions]: "Tasks & Actions",
  [ClientRoutes.ForYou]: "For You",
  [ClientRoutes.AdvisoryTeam]: "Advisory Team",
  [ClientRoutes.Investments]: "Managed by Earned",
  [ClientRoutes.TaxSavingsReport]: "Managed by Earned",
  [ClientRoutes.Accounts]: "Outside Accounts",
  [ClientRoutes.Spending]: "Spending",
  [ClientRoutes.Budgets]: "Budgets",
  [ClientRoutes.Settings]: "Settings",
  [ClientRoutes.Disclosures]: "Disclosures",
  [ClientRoutes.PhysicianOnFire]: "Physician On Fire",
  [ClientRoutes.TaxCenter]: "Tax Center",
  [ClientRoutes.ServiceLines]: "Additional Services",
  [ClientRoutes.Connections]: "Manage MX",
};

const HEADER_TITLE_BY_PATH_COBRANDED = {
  ...HEADER_TITLE_BY_PATH,
  [ClientRoutes.Overview]: "Dashboard",
};

type ColorVariants = {
  "primary": string;
  "secondary": string;
  "tertiary": string;
  "disabled": string;
  "white": string;
  "danger": string;
  "danger-light": string;
};

type ColorShades = {
  [key: string | number]: string & Record<never, never>;
};

type ThemeColors = DefaultColors & {
  [key in keyof DefaultColors]: string | ColorShades;
} & Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const fullConfig = resolveConfig(tailwindConfig as unknown as Config);
const themeColors = fullConfig.theme?.colors as ThemeColors;
const themeBorderColors = fullConfig.theme
  ?.borderColor as typeof tailwindConfig.theme.colors & ColorVariants;
const themeTextColors = fullConfig.theme
  ?.textColor as typeof tailwindConfig.theme.colors & ColorVariants;

const enum themeBreakpoints {
  lg = 1200,
  md = 996,
  sm = 768,
  xs = 480,
  xxs = 0,
}

const createFormData = (
  appendFile: Blob,
  fileName: string,
  folder: BoxFolder,
  rootFolderId: string,
  isTaxCenterUpload = false,
  returnId?: string
) => {
  const formData = new FormData();
  formData.append("file", appendFile);

  const attributes: any = {
    // eslint-disable-line @typescript-eslint/no-explicit-any
    name: fileName,
    parent: {
      id: folder.id.length > 0 ? folder.id : rootFolderId,
    },
  };

  if (isTaxCenterUpload) {
    const description: any = {
      SERVICE_LINE: "TAX",
    };
    if (returnId) {
      description.RETURN_ID = returnId;
    }
    attributes.description = JSON.stringify(description);
  }

  formData.append("attributes", JSON.stringify(attributes));

  return formData;
};

const enum HouseholdInvitationStatus {
  Pending = "Pending",
  Active = "Active",
  Expired = "Expired",
}

const enum CustomCookies {
  HasLoggedInBefore = "HasLoggedInBefore",
}

const enum ExternalLinks {
  PrivacyPolicy = "https://www.earnedwealth.com/legal/privacy-policy",
}

const CREATED_BY_ADVISOR_CLAIM = "https://earnedwealth.com/created-by-advisor";

const enum ZeroStateModules {
  FinancialPlan = "financial-plan",
  Accounts = "accounts",
  Investments = "investments",
  CobrandedExperience = "cobranded-experience",
  ThreeSixtyView = "360-view",
}

enum FeatureFlags {
  UMP_EXPERIENCE = "umpExperience",
  JMMG_EXPERIENCE = "jmmgExperience",
  MGP_WIZARD = "mgpWizard",
  MGP_WIZARD_ADVISOR = "mgpWizardAdvisor",
  TASKS_MANAGER = "tasksManager",
  ECMA_EXPERIENCE = "ecmaExperience",
  SJMG_EXPERIENCE = "sjmGExperience",
  TAX_CLIENT_PORTAL = "taxClientPortal",
  ATLAS_EXPERIENCE = "atlasExperience",
  ATLAS_DEMO_MODE = "atlasDemoMode",
  ATLAS_ADMINS = "atlasAdmins",
  SERVICE_LINE_PAGES = "serviceLinePages",
  TEAM_METRICS_DASHBOARD = "teamMetricsDashboard",
  // Gates the Advisor OS surface: the Command Center dashboard and Client 360.
  COMMAND_CENTER = "commandCenter",
  // A second gate ON TOP of COMMAND_CENTER for the Cases & Tasks page and its nav
  // link — both are required. It exists because the page depends on Atlas endpoints
  // that deploy from a different repo on a different cadence: without it, everyone
  // already holding commandCenter would see a page whose card expansion fails and
  // whose New Case modal offers no templates, from the moment the portal merges
  // until the backend does.
  //
  // Keep it after the backend is live, too — the page WRITES to Salesforce, so it
  // wants a kill switch that doesn't take the dashboard down with it.
  CASES_AND_TASKS = "casesAndTasks",
  // Gates the UXT Testing page and its nav link. In-flight UX is staged there
  // before it reaches the surface it derives from, so this is granted per
  // tester rather than rolled out. See docs/uxt-testing.md.
  //
  // The LaunchDarkly key is UXT_TESTING; the SDK camelCases it to this value.
  UXT_TESTING = "uxtTesting",
}

export {
  AdvisorRoutes,
  clientThreeSixtyHref,
  meetingBriefPrintHref,
  ClientRoutes,
  CareerStage,
  CareerStageExpanded,
  CareerStageMapping,
  CareerStageOptions,
  CustomCookies,
  ExternalLinks,
  HouseholdInvitationStatus,
  themeBreakpoints,
  themeColors,
  themeTextColors,
  themeBorderColors,
  createFormData,
  CREATED_BY_ADVISOR_CLAIM,
  ZeroStateModules,
  HEADER_TITLE_BY_PATH,
  HEADER_TITLE_BY_PATH_COBRANDED,
  FeatureFlags,
};
